// toast.js — Stacked toast notifications anchored under the top header.
//
// Public API:
//   window.gdbsToast(message, kind = 'info', opts = {})
//
// Args:
//   message  string | { title, body }
//   kind     'info' | 'success' | 'warn' | 'error'
//   opts:
//     timeoutMs  number (default 4500; 0 for sticky)
//     icon       fa class override (e.g. 'fas fa-flask')
//
// Returns:
//   dismiss()  function to remove the toast early
//
// Stack anchors under the system notice when it's visible, otherwise
// directly under the topbar. Up to 6 toasts queued; oldest auto-dismiss.

const ICON_BY_KIND = {
  info:    'fas fa-info-circle',
  success: 'fas fa-check-circle',
  warn:    'fas fa-exclamation-triangle',
  error:   'fas fa-times-circle',
};

const MAX_STACK = 6;

function ensureStack() {
  let s = document.getElementById('toast-stack');
  if (s) return s;
  s = document.createElement('div');
  s.id = 'toast-stack';
  document.body.appendChild(s);
  return s;
}

function refreshAnchor() {
  const s = document.getElementById('toast-stack');
  if (!s) return;
  const notice = document.getElementById('system-notice');
  const visible = notice && !notice.classList.contains('hidden');
  s.classList.toggle('below-notice', visible);
}

function prune() {
  const s = document.getElementById('toast-stack');
  if (!s) return;
  while (s.children.length > MAX_STACK) {
    s.firstElementChild?.remove();
  }
}

function makeToast(message, kind, opts) {
  const stack = ensureStack();
  refreshAnchor();
  const t = document.createElement('div');
  t.className = 'toast ' + kind;
  const iconCls = opts.icon || ICON_BY_KIND[kind] || ICON_BY_KIND.info;
  const isObj = message && typeof message === 'object';
  const title = isObj ? (message.title || '') : '';
  const body = isObj ? (message.body || '') : String(message);
  t.innerHTML =
    `<i class="${iconCls} toast-icon" aria-hidden="true"></i>` +
    `<div class="toast-body">${title ? `<strong>${escapeHtml(title)}</strong>` : ''}${escapeHtml(body)}</div>` +
    `<button class="toast-close" aria-label="Dismiss">&times;</button>`;

  stack.appendChild(t);
  prune();
  requestAnimationFrame(() => t.classList.add('show'));

  const dismiss = () => {
    t.classList.remove('show');
    setTimeout(() => t.remove(), 220);
  };
  t.querySelector('.toast-close').addEventListener('click', dismiss);

  const timeoutMs = opts.timeoutMs === undefined ? 4500 : opts.timeoutMs;
  if (timeoutMs > 0) {
    setTimeout(dismiss, timeoutMs);
  }
  return dismiss;
}

function escapeHtml(s) {
  return String(s).replace(/[&<>"']/g, c => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
  }[c]));
}

window.gdbsToast = (message, kind = 'info', opts = {}) =>
  makeToast(message, kind, opts);

// Convenience shorthands.
window.gdbsToastInfo    = (m, opts) => makeToast(m, 'info', opts || {});
window.gdbsToastSuccess = (m, opts) => makeToast(m, 'success', opts || {});
window.gdbsToastWarn    = (m, opts) => makeToast(m, 'warn', opts || {});
window.gdbsToastError   = (m, opts) => makeToast(m, 'error', opts || {});

// Re-anchor on notice-dismiss so toasts shift up to the topbar gap.
document.addEventListener('click', (ev) => {
  if (ev.target?.id === 'system-notice-close') {
    setTimeout(refreshAnchor, 50);
  }
});

export { makeToast as toast };
